import { CommonModule, formatCurrency, getCurrencySymbol } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DEFAULT_CURRENCY_CODE,
  EventEmitter,
  inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { MemberAccount } from '../../../../models/transactions/member-account';
import { ButtonTypeEnum } from '../../../../ui/buttons/button/button-type.enum';
import { MemberAccountStatusIdEnum } from '../../../../models/transactions/enum/member-account-status-id.enum';
import {
  struckAccountDescription,
  suspendedAccountDescription
} from '../../../../features/change-account-status/utils/account-status-date-utils';
import { IpAccountStatusMenuComponent } from '../../../../features/change-account-status/ip-account-status-menu/ip-account-status-menu.component';
import { StatusPillComponent } from '../../../../ui/pills/status-pill.component';
import { TextButtonComponent } from '../../../../ui/buttons/text-button/text-button.component';
import { LeadingZeroPipe } from '../../pipes/leading-zero.pipe';
import { AccountStatusPillPipe } from '../../pipes/account-status-pill.pipe';
import { spousalStatusPill } from '../../../../features/spousal-variable-benefit-rollover/utils/spousal-status-pill-utils';

const LONG_DATE: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };

@Component({
  selector: 'app-vb-account-summary',
  standalone: true,
  imports: [
    CommonModule,
    IpAccountStatusMenuComponent,
    StatusPillComponent,
    TextButtonComponent,
    LeadingZeroPipe,
    AccountStatusPillPipe
  ],
  templateUrl: './vb-account-summary.component.html',
  styleUrls: ['./vb-account-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbAccountSummaryComponent implements OnChanges {
  @Input({ required: true }) account!: MemberAccount;
  @Input() viewAccountDisabled: boolean = false;
  @Input() showStatusMenu = false;
  @Input() holderName: string | null = null;
  @Input() holderMemberId: number | null = null;
  @Output() viewAccountClicked = new EventEmitter<void>();
  @Output() statusSelected = new EventEmitter<MemberAccountStatusIdEnum>();

  private locale = inject(LOCALE_ID);
  private currencyCode = inject(DEFAULT_CURRENCY_CODE);

  public descriptionText = '';
  public balance = 0;
  public isSpousal = false;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.account) {
      this.descriptionText = this.description() ?? '';
      this.balance = this.account.isStruck() ? 0 : this.account.totalBalance;
      this.isSpousal = !!this.account.spousalRolloverSource || !!this.account.spousalRolloverSourceMemberName;
    }
  }

  private description(): string | null {
    if (this.account.isStruck()) {
      return struckAccountDescription(this.account, this.locale);
    }
    const suspendedDescription = suspendedAccountDescription(this.account, this.locale);
    if (suspendedDescription) {
      return suspendedDescription;
    }
    const details = this.account.variableBenefitAccountDetails;
    if (!details) {
      return null;
    }
    const effectiveDate = new Date(details.effectiveDate);
    if (this.account.statusId === MemberAccountStatusIdEnum.Pending || effectiveDate > new Date()) {
      const month = this.formatDay(effectiveDate, { month: 'long' });
      const year = effectiveDate.getUTCFullYear();
      return $localize`Effective ${month}, ${year}`;
    }
    if (this.account.statusId === MemberAccountStatusIdEnum.Inactive || !details.nextPaymentDate) {
      // TODO: show "Last Payment: [amount] in [month], [year]" once the account returns its last payment
      return null;
    }
    const amount = formatCurrency(
      details.nextPaymentAmount,
      this.locale,
      getCurrencySymbol(this.currencyCode, 'wide', this.locale),
      this.currencyCode
    );
    const date = this.formatDay(new Date(details.nextPaymentDate), LONG_DATE);
    return $localize`Next Payment: ${amount} on ${date}`;
  }

  private formatDay(day: Date, options: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(this.locale, { ...options, timeZone: 'UTC' }).format(day);
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
  protected readonly spousalPill = spousalStatusPill();
}
